import * as React from 'react';
import gql from 'graphql-tag';
import {graphql, InjectedGraphQLProps} from 'react-apollo';
import {Component, deps, inject, observer} from '../../lib/component';
import {classes, style} from 'typestyle';
import {important, linearGradient, percent, px, rem} from 'csx';
import {
  centerCenter,
  end,
  endJustified,
  horizontal,
  horizontallySpaced,
  start,
  startJustified,
  vertical,
  verticallySpaced,
  wrap
} from 'csstips';
import maybe from '../../lib/maybe';
import {SimilarTrack, Track} from '../../types/graphql';
import {shadowFirm} from '../../theme/shadow';
import {colorSubtle, colorWhite} from '../../theme/color';
import {mediaMobileOnly} from '../../theme/media';
import {ButtonPill} from '../button-pill';
import {ReleaseTracks, ReleaseTracksModel} from '../release-tracks';
import {BrandSponsoredLabelCompat} from '../compat/brand-sponsored-label-compat';
import {PlayButtonCompat} from '../compat/play-button-compat';
import {FavouriteButtonCompat} from '../compat/favourite-button-compat';
import {AddRemoveCompat} from '../compat/add-remove-compat';
import {Loading} from '../loading/loading';
import {ReleaseTile} from '../release-tile';
import {constantGoldenRatio} from '../../theme/constant';
import TrackPLine from './p_line';
import ReleaseCLine from '../release/c_line';

type CombinedProps = Props & InjectedGraphQLProps<Data>;

@inject(deps) @observer
class TrackInfo extends Component<CombinedProps> {
  render() {
    const {data} = this.props;
    const {handleClickLink} = this.props.controller.ui;
    const {preloadWaveformSvg, preloadImage} = this.props.controller.image;
    const {isMobile} = this.props.model.ui;

    if (data.loading) return (
      <div className={classes(TrackInfo.styles.container, TrackInfo.styles.loading)}>
        <Loading size={32} />
      </div>
    );

    const similar = maybe(
      () => data.getTrack!.similar!,
      []
    );

    const title = maybe(
      () => data.getTrack!.title!,
      null
    );

    const genres = maybe(
      () => data.getTrack!.tags!.hookd_genre!,
      []
    );

    const rightsholders = maybe(
      () => data.getTrack!.rightsholders! || [],
      []
    ).filter(r => r.is_listed);

    const tempo = maybe(
      () => data.getTrack!.tempo!,
      null
    );

    const bpm = maybe(
      () => data.getTrack!.bpm!,
      null
    );

    const path = maybe(
      () => `/music/artists/${data.getTrack!.artists[0].slug}/track/${data.getTrack!.slug}`,
      null
    );

    const release = maybe(
      () => data.getTrack!.releases![0],
      null
    );

    const cline = () => {
      if (release && release.c_line != null && release.c_line != "") {
        return release.c_line.replace(ReleaseCLine.symbol, "")
      }

      return parseInt(data.getTrack.available_at) + ' ' +data.getTrack.rightsholders[0].name
    };

    const pline = () => {
      if (data.getTrack && data.getTrack.p_line != null &&  data.getTrack.p_line != "") {
        return data.getTrack.p_line.replace(TrackPLine.symbol, "")
      }

      return parseInt(data.getTrack.available_at) + ' ' +data.getTrack.rightsholders[0].name
    };

    const combinedPCLine = ():string => {
      let c_line = cline();
      let p_line = pline();

      if (p_line == c_line) {
        return ReleaseCLine.symbol + ' ' + TrackPLine.symbol + c_line
      }

      return ReleaseCLine.symbol + ' ' + c_line + '<br />' + TrackPLine.symbol + ' ' + p_line
    }

    return (
      <div className={TrackInfo.styles.container}>
        <div className={TrackInfo.styles.details}>
          <div className={TrackInfo.styles.sections}>
            {genres && genres.length > 0 && (
              <div className={TrackInfo.styles.genres}>
                <h3>Genres</h3>
                <div>
                  {genres.map((genre, index) => (
                    <ButtonPill
                      muted={true}
                      href={`/search?genre=${genre.slug}`}
                      onClick={handleClickLink}
                      children={genre.name}
                      className={TrackInfo.styles.pill}
                      key={index.toString()}
                    />
                  ))}
                </div>
              </div>
            )}
            {rightsholders && rightsholders.length !== 0 && (
              <div className={TrackInfo.styles.genres}>
                <h3>Labels</h3>
                <div>
                  {rightsholders.map((rightsholder, index) => (
                    <ButtonPill
                      muted={true}
                      href={`/search?rightsholder=${rightsholder.name}`}
                      onClick={handleClickLink}
                      children={rightsholder.name}
                      className={TrackInfo.styles.pill}
                      key={index.toString()}
                    />
                  ))}
                </div>
              </div>
            )}
            {((tempo && tempo !== 'Unknown') || !!bpm) && (
              <div className={TrackInfo.styles.tempo}>
                <h3>Tempo</h3>
                <div>
                  {!!bpm && (
                    <ButtonPill
                      muted={true}
                      href={this.getBpmHref(bpm)}
                      onClick={this.props.controller.ui.handleClickLink}
                      children={`${bpm} BPM`}
                      className={TrackInfo.styles.pill}
                    />
                  )}
                  {!!tempo && (
                    <ButtonPill
                      muted={true}
                      href={this.getTempoHref(tempo)}
                      onClick={this.props.controller.ui.handleClickLink}
                      children={tempo}
                      className={TrackInfo.styles.pill}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
          {!!release && (
            <div className={TrackInfo.styles.release}>
              <h3>Release</h3>
              <ReleaseTile
                href={`/music/artists/${release.artist.slug}/release/${release.slug}`}
                preload={preloadImage}
                release={release}
                onClickLink={handleClickLink}
              />
            </div>
          )}
        </div>
        <div className={TrackInfo.styles.meta} dangerouslySetInnerHTML={{ __html: combinedPCLine() }}>
          </div>
        {similar && similar.length > 0 && (
          <h3>More songs like {title ? title : 'this'}</h3>
        )}
        {similar && similar.length && (
          <ReleaseTracks
            model={TrackInfo.releaseTracksModel(similar)}
            AddRemove={AddRemoveCompat}
            PlayButton={PlayButtonCompat}
            Favourite={FavouriteButtonCompat}
            BrandSponsoredLabel={BrandSponsoredLabelCompat}
            isMobile={isMobile}
            onClickLink={handleClickLink}
            preloadWaveform={preloadWaveformSvg}
            analytics={this.props.controller.analytics}
          />
        )}
        {path && (
          <div className={TrackInfo.styles.more}>
            <ButtonPill
              href={path}
              onClick={handleClickLink}
              className={TrackInfo.styles.pill}
              children={`More about ${title ? title : ''}`.trim()}
            />
          </div>
        )}
      </div>
    )
  }

  getTempoHref = (tempo: string) => {
    const nextTempoRange = TrackInfo.tempos.find(t => t.label.toLowerCase() === tempo.toLowerCase());

    if (nextTempoRange && nextTempoRange.range) {
      const [min, max] = nextTempoRange.range;

      return `/search?bpmMin=${min}&bpmMax=${max}`;
    }
  };

  getBpmHref = (bpm: number) => {
    return `/search?bpmMin=${bpm}&bpmMax=${bpm}`
  };

  static tempos = [
    {
      range: [0, 80],
      label: 'Very Slow',
    },
    {
      range: [80, 110],
      label: 'Slow',
    },
    {
      range: [110, 130],
      label: 'Medium',
    },
    {
      range: [130, 150],
      label: 'Fast',
    },
    {
      range: [150, 300],
      label: 'Very Fast',
    },
  ];

  static releaseTracksModel = (tracks: SimilarTrack[]): ReleaseTracksModel => ({
    loading: false,
    tracks: tracks
      .filter(t => !!t.track)
      .map(t => t.track as any),
  });

  static styles = {
    container: style({
      zIndex: 1,
      background: colorSubtle.fade(0.075).toString(),
      padding: rem(1),
      borderRadius: rem(1),
      $nest: {
        '& h3': {
          margin: '1.5rem 0 1rem'
        },
        '& h3:first-child': {
          margin: '0 0 1rem 0'
        }
      }
    }),
    loading: style({
      ...centerCenter,
      width: important('100%'),
      minHeight: px(240)
    }),
    preview: style({
      ...horizontal,
      ...endJustified,
      ...end,
      $nest: {
        '& > div': {
          width: percent(100),
          padding: rem(0.5),
          fontWeight: 'bold',
          textOverflow: 'ellipsis',
          textAlign: 'center',
          color: colorWhite.toString(),
          textShadow: shadowFirm
        }
      }
    }),
    details: style(
      {
        ...horizontal,
        ...horizontallySpaced(16),
        ...startJustified,
        ...start,
        position: 'relative'
      },
    ),
    tempo: style({
      flex: 1,
      $nest: {
        '& > div': {
          ...horizontal,
          ...horizontallySpaced(5),
          ...wrap
        }
      }
    }),
    genres: style({
      flex: 2,
      $nest: {
        '& > div': {
          ...horizontal,
          ...horizontallySpaced(5),
          ...wrap
        }
      }
    }),
    sections: style(
      {
        ...horizontal,
        ...horizontallySpaced(16),
        ...startJustified,
        ...start,
        flex: 1,
      },
      mediaMobileOnly({
        ...vertical,
        ...verticallySpaced(16)
      })
    ),
    release: style(
      {
        ...vertical,
        flexShrink: 0,
        width: px(160),
        $nest: {
          '& h3': {
            textAlign: 'right'
          }
        }
      },
      mediaMobileOnly({
        maxWidth: `${100 - 100 / constantGoldenRatio}%`
      })
    ),
    pill: style({
      fontSize: rem(0.75),
      margin: '0 0 5px 0',
      maxWidth: percent(90),
      whiteSpace: 'normal',
      textAlign: 'center'
    }),
    more: style({
      ...centerCenter
    }),
    meta: style({
      color: colorSubtle.toString(),
      fontSize: rem(0.875)
    })
  };
}

type Props = {
  identity: string;
}

type Data = {
  getTrack: Track
}

export default TrackInfo;

export const ConnectedTrackInfo = graphql(gql`
  query (
    $identity: ID!
  ) {
    getTrack(options:{identity:$identity}) {
      title
      slug
      artists {
        slug
      }
      releases {
        title
        slug
        c_line
        images {
          identity
        }
        artist {
          name
          slug
        }
      }
      tags {
        hookd_genre {
          slug
          name
        }
      }
      bpm
      tempo
      p_line
      available_at
      rightsholders {
        slug
        name
        is_listed
      }
      similar {
        track {
          audition_point
          identity
          title
          slug
          duration
          created_at
          is_charting
          is_featured
          is_new_release
          brand_sponsored
          branded_content
          audio {
            identity
          }
          images {
            identity
          }
          artists {
            images {
              identity
            }
            name
            slug
            identity
          }
        }
      }
    }
  }
`, {
  options: (variables: Props) => ({
    variables,
    // fetchPolicy: 'network-only'
  }),
})(TrackInfo);